import { ESdChangeReason, IComponent, IProperty } from "@kuika/kuika-cl-model";
import _ from "lodash";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { IApplyPropertyGridChangesToDisplayParameters } from "../../../../models/interface/function-parameters";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";

export interface IJsonEditorModalState {
  jsonSchema: any;
  isObjDetailsExpanded: boolean;
  selectedObjectDetails: any;
  isArrDetailsExpanded: boolean;
  setClassNameForCss: string | undefined;
}

export interface IJsonEditorModalProps {
  toggleJsonModal: any;
  property: IProperty;
  mappedProperty: IProperty;
  components: IComponent[];
  language: string;
  applyPropertyValueChanged: (params: IApplyPropertyGridChangesToDisplayParameters) => void;
}

class JsonEditorModal extends PureSdComponent<IJsonEditorModalProps & IWithSDProps, IJsonEditorModalState> {
  editor: any = null;

  constructor(props: IJsonEditorModalProps & IWithSDProps) {
    super(props);
    this.state = {
      jsonSchema: {},
      isObjDetailsExpanded: false,
      selectedObjectDetails: { index: -1 } as any,
      isArrDetailsExpanded: false,
      setClassNameForCss: ""
    };
  }

  createFormFromJSONSchema = (schema: string) => {
    if (schema) {
      const result = KMainFunctions.getCorrectJSONFromString(schema);
      let currentValue = KMainFunctions.getCorrectJSONFromString(this.props.property.symbol.fixedValue);
      if (Array.isArray(currentValue) === false) {
        currentValue = [currentValue];
      }
      if (KMainFunctions.isObjectEmpty(currentValue) === false) {
        if (result.type === "object") {
          result.items = {};
          result.items.properties = result.properties;
        }
        const properties = Object.keys(result.items.properties);
        result.kuikaProperties = properties;
        if (properties && properties.length > 0) {
          // result.firstColWidth = (720 / (properties.length + 1));
          result.firstColWidth = 80;

          if (properties.length > 15) {
            this.setState({ setClassNameForCss: "fit-content" });
          } else {
            this.setState({ setClassNameForCss: undefined });
          }
        }
        if (result.type === "array") {
          currentValue.forEach((c) => {
            Object.keys(c).forEach((cc) => {
              if (properties.indexOf(cc) === -1) {
                delete c[cc];
              }
            });
          });
        }
        result.default = currentValue;
        this.setState({ jsonSchema: result });
      }
    }
  };

  componentDidMount() {
    this.updateJsonModalData();
  }

  componentDidUpdate() {
    this.updateJsonModalData();
  }

  updateJsonModalData() {
    if (this.props.property && KMainFunctions.isObjectEmpty(this.props.mappedProperty) === true) {
      const propertyWithJsonSchema =
        this.props.property &&
        this.props.property.typeOptions &&
        this.props.property.typeOptions.find &&
        this.props.property.typeOptions.find((x) => x.jsonSchema && x.jsonSchema !== "");
      this.createFormFromJSONSchema(propertyWithJsonSchema?.jsonSchema);
    } else if (this.props.property && KMainFunctions.isObjectEmpty(this.props.mappedProperty) === false) {
      const originalPO =
        this.props.mappedProperty &&
        this.props.mappedProperty.typeOptions &&
        this.props.mappedProperty.typeOptions.find &&
        this.props.mappedProperty.typeOptions.find((p) => p.hasOwnJSONSchema === true);
      const mappedPO =
        this.props.property &&
        this.props.property.typeOptions &&
        this.props.property.typeOptions.find &&
        this.props.property.typeOptions.find((p) => p.hasOwnJSONSchema === false);
      if (originalPO && mappedPO) {
        const originalSchema = KMainFunctions.getCorrectJSONFromString(originalPO.jsonSchema);
        const mappedValue = KMainFunctions.getCorrectJSONFromString(this.props.mappedProperty.symbol.fixedValue);
        const newSchema = _.cloneDeep(originalSchema);
        newSchema.default = [];
        newSchema.title = "Data";
        newSchema.items.properties = {};
        if (mappedPO.mappingPath) {
          newSchema.items.title = "Data Entry";
          for (let i = 0; i < mappedValue.length; i++) {
            let x = mappedPO.mappingPath;
            x = x.replace("[]", `[${i}]`);
            let val = _.get(mappedValue, x);
            val += ".type";
            _.set(newSchema.items.properties, val, "string");
          }
        }
        if (mappedPO.extraJSONItems) {
          const extraKeys = mappedPO.extraJSONItems.split(",");
          for (let j = 0; j < extraKeys.length; j++) {
            const extraVal = `${extraKeys[j]}.type`;
            _.set(newSchema.items.properties, extraVal, "string");
          }
        }
        this.createFormFromJSONSchema(JSON.stringify(newSchema));
      }
    }
  }

  setValue = (value: any, propertyName: string, idx: number, ObjPropertyName?: string, arrIndex?: number) => {
    const defaultValues = this.state?.jsonSchema?.default;
    if (!ObjPropertyName) {
      defaultValues[idx][propertyName] = value;
    } else if (arrIndex !== undefined && arrIndex !== null) {
      if (!defaultValues[idx][ObjPropertyName][arrIndex]) {
        defaultValues[idx][ObjPropertyName][arrIndex] = {};
        defaultValues[idx][ObjPropertyName][arrIndex][propertyName] = value;
      } else {
        defaultValues[idx][ObjPropertyName][arrIndex][propertyName] = value;
      }
    } else if (!defaultValues[idx][ObjPropertyName]) {
      defaultValues[idx][ObjPropertyName] = {};
      defaultValues[idx][ObjPropertyName][propertyName] = value;
    } else {
      defaultValues[idx][ObjPropertyName][propertyName] = value;
    }
    const x = this.state.jsonSchema;
    x.default = defaultValues;
    this.setState({ jsonSchema: x });
  };

  updateJsonValueByEvent = (
    event: any,
    propertyName: string,
    idx: number,
    ObjPropertyName?: string,
    arrIndex?: number
  ) => {
    this.setValue(event.target.value, propertyName, idx, ObjPropertyName, arrIndex);
  };

  updateJsonValue = (value: any, propertyName: string, idx: number, ObjPropertyName?: string, arrIndex?: number) => {
    this.setValue(value, propertyName, idx, ObjPropertyName, arrIndex);
  };

  closeModal = () => {
    this.props.toggleJsonModal();
  };

  saveJSON = () => {
    let jsonValue: any = null;
    if (this.state.jsonSchema.type === "object") {
      jsonValue = JSON.stringify(this.state.jsonSchema.default[0]);
    } else if (this.state.jsonSchema.type === "array") {
      jsonValue = JSON.stringify(this.state.jsonSchema.default);
    }
    if (this.props.applyPropertyValueChanged)
      this.props.applyPropertyValueChanged({
        propertyName: this.props.property.name,
        value: jsonValue
      } as IApplyPropertyGridChangesToDisplayParameters);
    this.props.toggleJsonModal();
  };

  getPropertyType = (property: any) => {
    if (property && property.type) {
      if (property.options && property.options.length > 0) {
        return "options";
      }
      if (property.type === "object") {
        return "object";
      }
      return property.type;
    }
    return "string";
  };

  getProperty = (propertyName: string) => {
    const properties: any[] = this.state.jsonSchema.kuikaProperties;
    let property: any = null;
    if (properties.indexOf(propertyName) !== -1) {
      property = this.state.jsonSchema.items.properties[propertyName];
      return this.getPropertyType(property);
    }
    return "string";
  };

  printTextInput = (
    propertyName: string,
    index: number,
    defaultValue: any,
    ObjPropertyName?: string,
    arrIndex?: number
  ) => {
    return (
      <KuikaInput
        onChange={(e) => this.updateJsonValueByEvent(e, propertyName, index, ObjPropertyName, arrIndex)}
        type="text"
        value={defaultValue}
        title={propertyName}
        size="sm"
      />
    );
  };

  printNumberInput = (
    propertyName: string,
    index: number,
    defaultValue: any,
    ObjPropertyName?: string,
    arrIndex?: number
  ) => {
    return (
      <KuikaInput
        type="number"
        onChange={(_e, value) => this.updateJsonValue(value, propertyName, index, ObjPropertyName, arrIndex)}
        value={defaultValue}
        title={propertyName}
        size="sm"
      />
    );
  };

  printSwitch = (
    propertyName: string,
    index: number,
    defaultValue: any,
    ObjPropertyName?: string,
    arrIndex?: number
  ) => {
    return (
      <KuikaSwitch
        checked={defaultValue}
        onChange={(value) => this.updateJsonValue(value, propertyName, index, ObjPropertyName, arrIndex)}
        labelPosition="none"
      />
    );
  };

  printOptionsInput = (
    propertyName: string,
    index: number,
    defaultValue: any,
    ObjPropertyName?: string,
    arrIndex?: number
  ) => {
    let propertyOptions: any = null;
    if (ObjPropertyName) {
      const propertyType = this.getProperty(ObjPropertyName);
      if (propertyType === "array") {
        propertyOptions =
          this.state.jsonSchema?.items?.properties[ObjPropertyName].items.properties[propertyName].options;
      } else {
        propertyOptions = this.state.jsonSchema?.items?.properties[ObjPropertyName].properties[propertyName].options;
      }
    } else {
      propertyOptions = this.state.jsonSchema?.items?.properties[propertyName].options;
    }
    return (
      <KuikaSelectbox
        value={defaultValue}
        style={{ width: "100%" }}
        size="sm"
        onChange={(value) => this.updateJsonValue(value, propertyName, index, ObjPropertyName, arrIndex)}
        options={propertyOptions.map((po, idx: number): KuikaSelectboxOption => ({
          key: String(idx),
          value: po.value,
          label: po.key
        }))}
      />
    );
  };

  printComponentOptions = (
    propertyName: string,
    index: number,
    defaultValue: any,
    ObjPropertyName?: string,
    arrIndex?: number
  ) => {
    return (
      <KuikaSelectbox
        value={defaultValue}
        style={{ width: "100%" }}
        size="sm"
        searchable
        onChange={(value) => this.updateJsonValue(value, propertyName, index, ObjPropertyName, arrIndex)}
        options={this.props.components.map((comp): KuikaSelectboxOption => ({
          key: comp.name,
          value: comp.name,
          label: comp.name
        }))}
      />
    );
  };

  printObjectBtn = (propertyName: string, index: number) => {
    const resultJSX: any[] = [];
    resultJSX.push(
      <KuikaButton type="text" isIcon onClick={() => this.showObjInputs(propertyName, index)} aria-label="Edit">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
          <g fill="none" fillRule="evenodd">
            <path d="M0 0H24V24H0z" />
            <path
              fill="var(--dark-5)"
              fillRule="nonzero"
              d="M17.738 4c-.58 0-1.159.22-1.6.662L15.2 5.6l3.2 3.2.938-.938c.883-.883.883-2.316 0-3.2-.443-.442-1.022-.662-1.6-.662zM14 6.8l-8.731 8.731c-.3.3-.469.707-.469 1.131V19.2h2.537c.425 0 .832-.169 1.132-.469L17.2 10 14 6.8z"
            />
          </g>
        </svg>
      </KuikaButton>
    );
    return resultJSX;
  };

  printArrayBtn = (propertyName: string, index: number) => {
    const resultJSX: any[] = [];
    resultJSX.push(
      <KuikaButton type="text" isIcon onClick={() => this.showArrInputs(propertyName, index)} aria-label="Edit">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
          <g fill="none" fillRule="evenodd">
            <path d="M0 0H24V24H0z" />
            <path
              fill="var(--dark-5)"
              fillRule="nonzero"
              d="M17.738 4c-.58 0-1.159.22-1.6.662L15.2 5.6l3.2 3.2.938-.938c.883-.883.883-2.316 0-3.2-.443-.442-1.022-.662-1.6-.662zM14 6.8l-8.731 8.731c-.3.3-.469.707-.469 1.131V19.2h2.537c.425 0 .832-.169 1.132-.469L17.2 10 14 6.8z"
            />
          </g>
        </svg>
      </KuikaButton>
    );
    return resultJSX;
  };

  showObjInputs = (propertyName: string, index: any) => {
    const objDetails = {} as any;
    const keys = Object.keys(this.state.jsonSchema.items.properties[propertyName].properties);
    let width = 0;
    objDetails.propertyName = propertyName;
    objDetails.index = index;
    const visibility = !this.state.isObjDetailsExpanded;
    let arrVisibility = this.state.isArrDetailsExpanded;
    if (visibility === true) {
      arrVisibility = false;
    }
    if (keys && keys.length > 0) {
      width = 720 / (keys.length + 1) - 10;
    }
    objDetails.colWidth = width;
    this.setState({
      isObjDetailsExpanded: visibility,
      selectedObjectDetails: objDetails,
      isArrDetailsExpanded: arrVisibility
    });
  };

  showArrInputs = (propertyName: string, index: any) => {
    const objDetails = {} as any;
    let keys: any[] = [];
    let width = 0;
    objDetails.propertyName = propertyName;
    objDetails.index = index;
    if (!this.state.jsonSchema.default[index][propertyName]) {
      const type = this.getProperty(propertyName);
      if (type === "array") {
        const js = this.state.jsonSchema;
        js.default[index][propertyName] = [];

        this.setState({ jsonSchema: js });
      } else if (type === "object") {
        const js = this.state.jsonSchema;
        js.default[index][propertyName] = {};

        this.setState({ jsonSchema: js });
      }
    }
    const visibility = !this.state.isArrDetailsExpanded;
    let objVisibility = this.state.isObjDetailsExpanded;
    if (visibility === true) {
      objVisibility = false;
    }

    keys = Object.keys(this.state.jsonSchema.items.properties[propertyName].items.properties);
    if (keys && keys.length > 0) {
      width = 720 / (keys.length + 1) - 10;
    }

    objDetails.colWidth = width;
    this.setState({
      isArrDetailsExpanded: visibility,
      selectedObjectDetails: objDetails,
      isObjDetailsExpanded: objVisibility
    });
  };

  printObjectInputs = () => {
    const resultJSX: any[] = [];
    const property = this.state.jsonSchema?.items?.properties[this.state.selectedObjectDetails.propertyName];
    const defaultValues =
      this.state.jsonSchema?.default[this.state.selectedObjectDetails.index][
        this.state.selectedObjectDetails.propertyName
      ];
    if (property && property.properties) {
      const properties = Object.keys(property.properties);
      properties.forEach((p) => {
        const curProp = property.properties[p];
        const propType = this.getPropertyType(curProp);
        let dValue: any = "";
        if (defaultValues && defaultValues[p]) {
          dValue = defaultValues[p];
        }
        const jsx = this.printProperInputs(
          p,
          this.state.selectedObjectDetails.index,
          dValue,
          propType,
          this.state.selectedObjectDetails.propertyName
        );
        resultJSX.push(jsx);
      });
    }
    return resultJSX;
  };

  getPropertiesForArray = () => {
    let properties: any[] = [];
    const property = this.state.jsonSchema?.items?.properties[this.state.selectedObjectDetails.propertyName];
    if (property) {
      properties = Object.keys(property.items.properties);
    }
    return properties;
  };

  printProperLabels = (propertyName: string, type = "") => {
    let resultJSX: any[] = [];
    resultJSX.push(
      <div className="k_col" style={{ width: this.state.jsonSchema.firstColWidth, flex: 1 }} title={propertyName}>
        <label className="k_colLabel">{propertyName}</label>
      </div>
    );
    if (type === "object" || type === "array") {
      let keys: any[] = [];
      if (type === "array") {
        keys = Object.keys(this.state.jsonSchema.items.properties[propertyName].items.properties);
      } else {
        keys = Object.keys(this.state.jsonSchema.items.properties[propertyName].properties);
      }
      if (keys && keys.length > 0) {
        resultJSX = [];
        keys.forEach((k) => {
          resultJSX.push(
            <div className="k_col" style={{ width: this.state.selectedObjectDetails.colWidth }}>
              <label className="k_colLabel">{k}</label>
            </div>
          );
        });
      }
    }
    return resultJSX;
  };

  printProperInputs = (
    propertyName: string,
    index: number,
    defaultValue: any,
    propType?: string,
    ObjPropertyName?: any,
    arrIndex?: number
  ) => {
    let propertyType = "";
    if (!propType) {
      propertyType = this.getProperty(propertyName);
    } else {
      propertyType = propType;
    }
    const resultJSX: any[] = [];
    switch (propertyType) {
      case "string":
        resultJSX.push(this.printTextInput(propertyName, index, defaultValue, ObjPropertyName, arrIndex));
        break;
      case "integer":
        resultJSX.push(this.printNumberInput(propertyName, index, defaultValue, ObjPropertyName, arrIndex));
        break;
      case "options":
        resultJSX.push(this.printOptionsInput(propertyName, index, defaultValue, ObjPropertyName, arrIndex));
        break;
      case "boolean":
        resultJSX.push(this.printSwitch(propertyName, index, defaultValue, ObjPropertyName, arrIndex));
        break;
      case "component":
        resultJSX.push(this.printComponentOptions(propertyName, index, defaultValue, ObjPropertyName, arrIndex));
        break;
      case "object":
        resultJSX.push(this.printObjectBtn(propertyName, index));
        break;
      case "array":
        resultJSX.push(this.printArrayBtn(propertyName, index));
        break;
    }
    return resultJSX;
  };

  printDeleteBtn = (index: number, arrIndex?: number, type?: string) => {
    let width = this.state.jsonSchema.firstColWidth;
    if (type) {
      width = this.state.selectedObjectDetails.colWidth;
    }
    return (
      <div className="k_col" style={{ width, flex: 1, textAlign: "end" }}>
        <KuikaButton type="text" isIcon onClick={() => this.deleteRow(index, arrIndex, type)} aria-label="Delete">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            className="kuika_json_editor_delete_button"
          >
            <g fill="none" fillRule="evenodd">
              <g>
                <path d="M0 0H24V24H0z" />
                <g fill="var(--dark-5)" fillRule="nonzero">
                  <path
                    d="M8.075 0c-1.4 0-2.55 1.15-2.55 2.55H2.867c-.038-.006-.076-.01-.114-.01-.031.002-.062.005-.093.01H1.062c-.23-.003-.443.118-.56.316-.115.199-.115.444 0 .643.117.198.33.32.56.316h1.063v10.837C2.125 15.946 3.179 17 4.463 17h7.224c1.284 0 2.338-1.054 2.338-2.338V3.825h1.063c.23.003.443-.118.56-.316.115-.199.115-.444 0-.643-.117-.198-.33-.32-.56-.316h-1.596c-.068-.011-.138-.011-.207 0h-2.66c0-1.4-1.15-2.55-2.55-2.55zm0 1.275c.712 0 1.275.563 1.275 1.275H6.8c0-.712.563-1.275 1.275-1.275zM3.4 3.825h9.35v10.837c0 .595-.468 1.063-1.063 1.063H4.463c-.595 0-1.063-.468-1.063-1.063V3.825zm3.178 2.116c-.352.005-.633.295-.628.647v6.375c-.003.23.118.443.316.56.199.115.444.115.643 0 .198-.117.32-.33.316-.56V6.588c.002-.173-.065-.339-.187-.46-.122-.123-.288-.19-.46-.187zm2.975 0c-.352.005-.633.295-.628.647v6.375c-.003.23.118.443.316.56.199.115.444.115.643 0 .198-.117.32-.33.316-.56V6.588c.002-.173-.065-.339-.187-.46-.122-.123-.288-.19-.46-.187z"
                    transform="translate(4 3.5)"
                  />
                </g>
              </g>
            </g>
          </svg>
        </KuikaButton>
      </div>
    );
  };

  printAddSection = (title: any, isItInnerArray: boolean, type?: string) => {
    return (
      <div className="k_addSections" style={{ display: "inline-block" }}>
        {type && type === "object" ? (
          ""
        ) : (
          <KuikaButton size="md" type="secondary" onClick={() => this.addRow(title, isItInnerArray)}>
            {KMainFunctions.getMultilanguageValue("Add", this.props.language)} +
          </KuikaButton>
        )}
      </div>
    );
  };

  addRow = (title: string, isItInnerArray: boolean) => {
    const newValues = this.state?.jsonSchema;
    if (isItInnerArray === true) {
      const arr: any[] = [];
      if (Array.isArray(newValues.default[this.state.selectedObjectDetails.index][title]) === false) {
        arr.push(newValues.default[this.state.selectedObjectDetails.index][title]);
        newValues.default[this.state.selectedObjectDetails.index][title] = arr;
      } else {
        newValues.default[this.state.selectedObjectDetails.index][title].push({});
      }
    } else {
      if (newValues.default === undefined) {
        newValues.default = [];
      }
      newValues.default.push({});
    }
    this.setState({ jsonSchema: newValues });
  };

  deleteRow = (idx: number, arrIndex?: number, type?: string) => {
    const newValues = this.state?.jsonSchema;
    let isObjectDetailsExpanded = this.state.isObjDetailsExpanded;
    if (type === "object") {
      newValues.default[this.state.selectedObjectDetails.index][this.state.selectedObjectDetails.propertyName] = {};
      isObjectDetailsExpanded = !isObjectDetailsExpanded;
      this.setState({ selectedObjectDetails: {} });
    } else if (arrIndex === undefined || arrIndex === null) {
      newValues.default.splice(idx, 1);
      this.setState({ selectedObjectDetails: {} });
    } else {
      newValues.default[this.state.selectedObjectDetails.index][this.state.selectedObjectDetails.propertyName].splice(
        idx,
        1
      );
    }
    this.setState({
      jsonSchema: newValues,
      isObjDetailsExpanded: isObjectDetailsExpanded
    });
  };

  render() {
    return (
      <>
        {
          <KModal
            showModal
            closeModal={this.closeModal}
            header={this.props?.property?.name}
            footer={
              <KModalFooter
                onCancel={this.closeModal}
                cancelLabel={KMainFunctions.getMultilanguageValue("Cancel", this.props.language)}
                cancelTestId="jsonEditorCancelButton"
                submitLabel={KMainFunctions.getMultilanguageValue("Save", this.props.language)}
                submitTestId="jsonEditorSaveButton"
                onSubmit={() => this.saveJSON()}
              />
            }
            width={1520}
            centered
          >
            <div>
              <div id="editor_holder">
                <DragDropContext
                  onDragEnd={(param) => {
                    const srcI = param.source.index;
                    const desI = param.destination?.index;
                    const x = this.state.jsonSchema;
                    const oldData = [...this.state.jsonSchema.default];

                    if (desI === undefined || desI === null) {
                      return;
                    }

                    oldData.splice(desI, 0, oldData.splice(srcI, 1)[0]);
                    x.default = oldData;
                    this.setState({ jsonSchema: x });
                  }}
                >
                  <div
                    style={{
                      overflowY: "auto",
                      height: "450px",
                      margin: "15px"
                    }}
                  >
                    <div
                      className="k_row"
                      style={{
                        textAlign: "center",
                        position: "sticky",
                        top: 0,
                        backgroundColor: "var(--blue-05)",
                        zIndex: 1,
                        border: "var(--border)",
                        borderRadius: "var(--radius-s)",
                        width: this.state.setClassNameForCss
                      }}
                    >
                      {this.state?.jsonSchema?.kuikaProperties?.map((p, idx) => {
                        return this.printProperLabels(p);
                      })}
                      <div
                        className="k_col"
                        style={{
                          width: this.state.jsonSchema.firstColWidth,
                          flex: 1,
                          textAlign: "end"
                        }}
                      >
                        <label className="k_colLabel" style={{ marginRight: "10px" }}>
                          Action
                        </label>
                      </div>
                    </div>

                    <Droppable droppableId="droppable-1">
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                          {this.state?.jsonSchema?.default?.map((d, dIdx) => {
                            return (
                              <Draggable key={dIdx} draggableId={`draggable-${dIdx}`} index={dIdx}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={{
                                      ...provided.draggableProps.style,
                                      boxShadow: snapshot.isDragging ? "var(--shadow-md)" : "none"
                                    }}
                                  >
                                    <div
                                      className="k_row"
                                      style={{
                                        width: this.state.setClassNameForCss
                                      }}
                                      {...provided.dragHandleProps}
                                    >
                                      {this.state?.jsonSchema?.kuikaProperties?.map((p, idx) => {
                                        return (
                                          <div
                                            key={idx}
                                            className="k_col"
                                            style={{
                                              width: this.state.jsonSchema.firstColWidth,
                                              flex: 1
                                            }}
                                          >
                                            {this.printProperInputs(p, dIdx, d[p])}
                                          </div>
                                        );
                                      })}
                                      {this.printDeleteBtn(dIdx)}
                                    </div>
                                    {this.state.selectedObjectDetails.index === dIdx && (
                                      <div>
                                        {this.state.isObjDetailsExpanded === true && (
                                          <div>
                                            <div
                                              className="k_row k_objDetails"
                                              style={{
                                                width: this.state.setClassNameForCss
                                              }}
                                            >
                                              {this.printProperLabels(
                                                this.state.selectedObjectDetails.propertyName,
                                                "object"
                                              )}
                                              <div
                                                className="k_col"
                                                style={{
                                                  width: this.state.selectedObjectDetails.colWidth
                                                }}
                                              >
                                                <label className="k_colLabel">Action</label>
                                              </div>
                                            </div>
                                            <div
                                              className="k_row k_objDetails"
                                              style={{
                                                width: this.state.setClassNameForCss
                                              }}
                                            >
                                              {this.printObjectInputs().map((input, idx) => {
                                                return (
                                                  <div
                                                    key={idx}
                                                    className="k_col"
                                                    style={{
                                                      width: this.state.selectedObjectDetails.colWidth
                                                    }}
                                                  >
                                                    {input}
                                                  </div>
                                                );
                                              })}
                                              {this.printDeleteBtn(0, 0, "object")}
                                            </div>
                                          </div>
                                        )}
                                        {this.state.isArrDetailsExpanded === true && (
                                          <div>
                                            <div
                                              className="k_row k_objDetails"
                                              style={{
                                                width: this.state.setClassNameForCss
                                              }}
                                            >
                                              {this.printProperLabels(
                                                this.state.selectedObjectDetails.propertyName,
                                                "array"
                                              )}
                                              <div
                                                className="k_col"
                                                style={{
                                                  width: this.state.selectedObjectDetails.colWidth
                                                }}
                                              >
                                                <label className="k_colLabel">Action</label>
                                              </div>
                                            </div>
                                            <div className="k_objDetails">
                                              {this.state.jsonSchema?.default[this.state.selectedObjectDetails.index][
                                                this.state.selectedObjectDetails.propertyName
                                              ].map((dd, index) => {
                                                return (
                                                  <div
                                                    className="k_row"
                                                    style={{
                                                      width: this.state.setClassNameForCss
                                                    }}
                                                  >
                                                    {this.getPropertiesForArray().map((pp, idx) => {
                                                      return (
                                                        <div
                                                          key={dIdx + idx}
                                                          className="k_col"
                                                          style={{
                                                            width: this.state.selectedObjectDetails.colWidth
                                                          }}
                                                        >
                                                          {this.printProperInputs(
                                                            pp,
                                                            this.state.selectedObjectDetails.index,
                                                            dd[pp],
                                                            this.getPropertyType(
                                                              this.state.jsonSchema.items.properties[
                                                                this.state.selectedObjectDetails.propertyName
                                                              ].items.properties[pp]
                                                            ),
                                                            this.state.selectedObjectDetails.propertyName,
                                                            index
                                                          )}
                                                        </div>
                                                      );
                                                    })}
                                                    {this.printDeleteBtn(index, index, "array")}
                                                  </div>
                                                );
                                              })}
                                              <div
                                                className="k_addSections"
                                                style={{
                                                  justifyContent: "flex-end",
                                                  paddingBottom: 10,
                                                  paddingRight: 5
                                                }}
                                              >
                                                <KuikaButton
                                                  size="sm"
                                                  type="secondary"
                                                  onClick={() =>
                                                    this.addRow(this.state.selectedObjectDetails.propertyName, true)
                                                  }
                                                >
                                                  {KMainFunctions.getMultilanguageValue("Add", this.props.language)} +
                                                </KuikaButton>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                        </div>
                      )}
                    </Droppable>
                  </div>
                </DragDropContext>
              </div>
              {/* The scroller above already carries a 15px margin on every side, so the last
                  row before the footer has none of its own — pad it out to match. */}
              <div style={{ marginBottom: "var(--gap-l)" }}>
                {this.printAddSection(this.state.jsonSchema.title, false, this.state.jsonSchema.type)}
              </div>
            </div>
          </KModal>
        }
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const jsonEditorModal = withSD<IJsonEditorModalProps>(JsonEditorModal);
export { jsonEditorModal as JsonEditorModal };
